import { axiosInstance } from './axiosInstance';
import type {
  Client,
  PaginatedResponse,
  PagedApiResponse,
  GetClientsParams,
  CreateClientPayload,
  UpdateClientPayload,
  StatCounts,
} from '../shared/types';

export type { GetClientsParams, StatCounts };

// ── Real API: Get all tenants/clients (server-side pagination) ──────

export async function getClients(params: GetClientsParams): Promise<PaginatedResponse<Client>> {
  const queryParams: Record<string, string | number> = {
    page: params.page - 1,
    size: params.pageSize,
    sort: params.sortBy ? `${params.sortBy},${params.sortOrder || 'asc'}` : 'name,asc',
  };

  if (params.search) {
    queryParams.search = params.search;
  }

  if (params.status) {
    queryParams.status = params.status;
  }

  if (params.createdAt) {
    queryParams.createdAt = params.createdAt;
  }

  if (params.projectId) {
    queryParams.projectId = params.projectId;
  }

  const response = await axiosInstance.get<PagedApiResponse<Client>>('/tenants', {
    params: queryParams,
  });

  const { content, totalElements, totalPages } = response.data;

  return {
    data: content,
    meta: {
      total: totalElements,
      page: params.page,
      pageSize: params.pageSize,
      totalPages,
    },
  };
}

// ── Real API: Get client options for dropdowns ──────────────────────

/** `projectId` scopes the list to the clients belonging to one project. */
export async function getClientOptions(
  search?: string,
  projectId?: string,
): Promise<
  {
    id: string;
    name: string;
    connectorIds?: string[];
    subscriptionPlan?: Client['subscriptionPlan'];
    project?: Client['project'];
  }[]
> {
  const params: Record<string, string | number> = { page: 0, size: 50, sort: 'name,asc' };
  if (search) {
    params.search = search;
  }

  if (projectId) {
    params.projectId = projectId;
  }
  const response = await axiosInstance.get<PagedApiResponse<Client>>('/tenants', { params });
  return response.data.content.map((c) => ({
    id: c.id,
    name: c.name,
    connectorIds: c.connectorIds || [],
    subscriptionPlan: c.subscriptionPlan || null,
    // Callers decide behaviour from the client's project — see CreateUserPage.
    project: c.project ?? null,
  }));
}

// ── Real API: Get client/tenant by ID ───────────────────────────────

export async function getClientById(id: string): Promise<Client> {
  const response = await axiosInstance.get<Client>(`/tenants/${id}`);
  return response.data;
}

// ── Real API: Create a client/tenant ────────────────────────────────

export async function createClient(payload: CreateClientPayload): Promise<Client> {
  const response = await axiosInstance.post<Client>('/tenants', payload);
  return response.data;
}
export async function updateClient(id: string, payload: UpdateClientPayload): Promise<Client> {
  const response = await axiosInstance.patch<Client>(`/tenants/${id}`, payload);
  return response.data;
}

export async function getStatCounts(tenantId: string): Promise<StatCounts> {
  const response = await axiosInstance.get<StatCounts>('/stats/counts', { params: { tenantId } });
  return response.data;
}
